import { QuestionEntry } from '@wix/ambassador-faq-question-entry-v2-question-entry/types';
import React, { useContext, useState } from 'react';
import {
  Accordion,
  AccordionSize,
  useTPAComponentsContext,
} from '@wix/wix-ui-tpa/cssVars';
import SocialWrapper from '../../SocialWrapper/SocialWrapper';
import AccordionNumberStrip from './AccordionNumberStrip';
import { st, classes } from '../../Widget.st.css';
import settingsParams from '../../../settingsParams';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import {
  st as accordionStyle,
  classes as accordionClasses,
} from './numbered.st.css';
import RCV from '../../RCV';
import { DraftJSObject } from '../../../../../types/RichContent';
import { AppContext } from '../../SettingsProvider/SettingsProvider';
import { openIconBySettings } from '../../../../../utils/openIconBySettings';
import { useBi } from '@wix/yoshi-flow-editor';
import { faqQuestionExpanded } from '@wix/bi-logger-labs-data/v2';
import { DATA_HOOKS } from '../../../../../constants/dataHooksElements';
import stylesParams from '../../../stylesParams';
import { useLayoutType } from '../../../../../hooks/useLayoutType';

const NumberOneColumn = ({
  filteredQuestions,
  indexes,
}: {
  filteredQuestions: QuestionEntry[];
  indexes: number[];
}) => {
  const logger = useBi();
  const { mobile } = useTPAComponentsContext();
  const styles = useStyles();
  const questionFontParam = styles.get(stylesParams.questionTextFont);
  const settings = useSettings();
  const noAnimation = !settings.get(settingsParams.enableScrollAnimation);
  const openOneAnswer = settings.get(settingsParams.openOneAnswer);
  const { firstQuestionId, baseURL, isEditor3 } = useContext(AppContext);
  const [expandedId, setExpandedId] = useState<string | undefined>(
    firstQuestionId,
  );
  const stRootClass = accordionStyle(accordionClasses.root, { noAnimation });
  const {
    openQuestionIcon,
    layout: { layoutValue },
    spacing: { gap },
  } = useLayoutType(isEditor3);
  const icons = openIconBySettings(openQuestionIcon);
  const transparentDivider = accordionStyle(
    accordionClasses.transparentDivider,
    { mobile },
  );

  const handleAccordionItemClick = (item: string) => {
    const questionData = filteredQuestions.find(
      (q: QuestionEntry) => q.id === item,
    );

    if (questionData) {
      const { question, categoryId, sortOrder } = questionData;
      logger.report(
        faqQuestionExpanded({
          type: layoutValue,
          question_id: item,
          question_name: question || '',
          category_id: categoryId || '',
          question_order: sortOrder || 0,
          url: baseURL || '',
          isOoi: true,
        }),
      );
    }
  };

  const handleItemClick = (itemId: string, isExpanded: boolean) => {
    if (isExpanded) {
      handleAccordionItemClick(itemId);
      if (openOneAnswer) {
        setExpandedId(itemId);
      }
    } else if (openOneAnswer) {
      setExpandedId(undefined);
    }
  };

  return (
    <ul
      data-hook={DATA_HOOKS.NUMBERED_ACCORDION}
      className={st(classes.numberLayout, {
        mobile,
      })}
    >
      {filteredQuestions.map((item: QuestionEntry, index) => {
        const { id, question, draftjs } = item;
        const parsedDraftjs: DraftJSObject = JSON.parse(draftjs!);

        return (
          <li
            className={st(classes.numberItem, { novspace: gap === 0 })}
            key={id!}
          >
            <AccordionNumberStrip index={indexes[index] - 1} />
            <Accordion
              data-hook={`${DATA_HOOKS.NUMBERED_ACCORDION_ACCORDION}_${id}`}
              size={AccordionSize.Small}
              key={`numbered-${expandedId}-${id}`}
              className={accordionStyle(stRootClass, transparentDivider)}
              initiallyExpanded={
                openOneAnswer
                  ? id === expandedId
                    ? id
                    : undefined
                  : firstQuestionId === id
                  ? firstQuestionId
                  : undefined
              }
              icons={icons}
              onItemClick={handleItemClick}
            >
              <Accordion.Item
                key={id!}
                id={id!}
                title={question!}
                titleTag={questionFontParam.htmlTag}
              >
                {parsedDraftjs && <RCV convertAnswerToJSON={parsedDraftjs} />}

                <SocialWrapper questionId={id!} />
              </Accordion.Item>
            </Accordion>
          </li>
        );
      })}
    </ul>
  );
};

export default NumberOneColumn;
